import React from 'react';
import ThemeProvider from '@design-system/theme/ThemeContext';
import * as S from 'src/pages/auth/layout/layout.styles';
import { LayoutProps } from 'src/pages/auth/layout/layout.types';
import { Link } from '@design-system/atoms/link';
import { OverlayProvider } from 'react-aria';
import { Modal } from '@design-system/molecules/modal';
import { PDFViewer } from 'src/components/common/PDFViewer';
import { useLocation, useHistory } from 'react-router';
import { Link as RouterLink } from 'react-router-dom';
import { appConfig } from 'src/config';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { AppState } from 'src/reducers';
import { SupportedLang } from 'src/messages';
import { setLocale } from 'src/reducers/slices/locale';
import { Icon } from '@design-system/atoms/icon';
import { ReduxToastProvider } from 'src/pages/auth/layout/ReduxToastProvider';
import { FormattedMessage, useIntl } from 'react-intl';
import useHopscotch from 'src/hooks/useHopscotch';

const isValidProfile =
  appConfig.APP_ID === 'smsportal-gms-aws-zhr' ||
  appConfig.APP_ID === 'smsportal-gms-cloud' ||
  appConfig.APP_ID === 'smsportal-uat' ||
  appConfig.APP_ID === 'smsportal-dev';

const LanguagePicker = () => {
  const appLang = useSelector((state: AppState) => state.locale.lang);
  const dispatch = useDispatch();

  return (
    <S.LanguagePicker>
      <Icon name="language" size={24} />
      <div>
        {Object.values(SupportedLang).map((lang) => (
          <S.LanguageButton key={lang} $isSelected={appLang === lang} onClick={() => dispatch(setLocale(lang))}>
            <S.LanguageText variant="body-16" as="span">
              {lang.toUpperCase()}
            </S.LanguageText>
          </S.LanguageButton>
        ))}
      </div>
    </S.LanguagePicker>
  );
};

const TermsConditionsDialog = () => {
  const location = useLocation();
  const history = useHistory();
  const { formatMessage } = useIntl();
  const appLang = useSelector((state: AppState) => state.locale.lang);

  const hash = location.hash?.substring(1);

  const handleClose = () => {
    // Navigate to the same path but without the hash
    history.push(location.pathname + location.search);
  };

  return (
    <Modal
      isOpen={Boolean(hash)}
      width={900}
      onClose={handleClose}
      title={formatMessage({ id: 'dashboard.terms.conditions' })}
    >
      <PDFViewer
        file={
          hash !== 'data-processing'
            ? appLang === SupportedLang.DE
              ? '/documents/TermsConditions_DE.pdf'
              : appLang === SupportedLang.FR
                ? '/documents/TermsConditions_FR.pdf'
                : '/documents/TermsConditions_EN.pdf'
            : appLang === SupportedLang.FR
              ? '/documents/DataProcessing_FR.pdf'
              : '/documents/DataProcessing_EN.pdf'
        }
        zoom={1.28}
      />
    </Modal>
  );
};

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  useHopscotch();

  return (
    <ThemeProvider initialTheme="light">
      <OverlayProvider>
        <ReduxToastProvider>
          <TermsConditionsDialog />
          <S.Layout>
            <S.LeftSection>
              <S.LeftHeader>
                <img src="/LogoHeader.png" alt="GMS Logo" />
                <LanguagePicker />
              </S.LeftHeader>
              <S.LeftContent>{children}</S.LeftContent>
              <S.LeftFooter>
                <div style={{ display: 'flex', gap: '16px' }}>
                  <Link as={RouterLink} to="#terms-conditions" variant="subtle" size={12}>
                    <FormattedMessage id="dashboard.terms.conditions" />
                  </Link>
                  <Link as={RouterLink} to="#data-processing" variant="subtle" size={12}>
                    <FormattedMessage id="dashboard.data.processing" />
                  </Link>
                </div>
                <S.CopyRights variant="body-16" as="p">
                  © {new Date().getFullYear()} GMS
                </S.CopyRights>
              </S.LeftFooter>
            </S.LeftSection>
            <S.RightSection>
              <S.RightContent>
                <S.ImageContainer>
                  <img src="/login.png" alt="GMS Campaign Platform on multiple devices" width="100%" />
                </S.ImageContainer>
                <div style={{ minHeight: '165px' }}>
                  <S.Header variant="h2">
                    <FormattedMessage id="login.hero.title" />
                  </S.Header>
                  <S.Body variant="body-16" style={{ marginTop: '16px' }}>
                    <FormattedMessage id="login.hero.description" />
                  </S.Body>
                </div>
              </S.RightContent>
            </S.RightSection>
          </S.Layout>
          {isValidProfile && (
            <script
              id="CookieDeclaration"
              src="https://consent.cookiebot.com/c529d398-d2f7-4724-8373-cc408e8649bc/cd.js"
              type="text/javascript"
              async
            ></script>
          )}
        </ReduxToastProvider>
      </OverlayProvider>
    </ThemeProvider>
  );
};
